import { useEffect } from 'react';

import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';

import Loader from 'components/Loader';
import { useDispatch } from 'react-redux';
import { updateMenuList } from 'store/reducers/menu';
import { GuardProps } from 'types/auth';
import { NavItemType } from 'types/menu';

function sanitizeMenuItems(items: NavItemType[] = []): NavItemType[] {
  return items.map((item) => {
    const nextItem: NavItemType = { ...item };
    const url = typeof nextItem.url === 'string' ? nextItem.url.trim() : '';
    if (url && /(^|\/)(undefined|null)(\/|$|[?#])/i.test(url)) {
      nextItem.url = undefined;
    }
    if (Array.isArray(nextItem.children)) {
      nextItem.children = sanitizeMenuItems(nextItem.children);
    }
    return nextItem;
  });
}

function parseMenuFromStorage(): { items: NavItemType[] } | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('loginMenu');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { items: NavItemType[] };
    const sanitizedItems = sanitizeMenuItems(parsed?.items);
    localStorage.setItem('loginMenu', JSON.stringify({ ...parsed, items: sanitizedItems }));
    return { items: sanitizedItems };
  } catch {
    return null;
  }
}

const AuthGuard = ({ children }: GuardProps) => {
  const { data: session, status } = useSession();
  const router = useRouter();
  const dispatch = useDispatch();

  useEffect(() => {
    if (status === 'loading') return;

    if (!session) {
      router.push('/login');
      return;
    }

    const menuData = parseMenuFromStorage();
    if (menuData?.items && menuData.items.length > 0) {
      dispatch(updateMenuList(menuData));
    }
  }, [session, status, dispatch, router]);

  if (status === 'loading' || !session?.user) return <Loader />;

  return children;
};

export default AuthGuard;
